import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes, faSpinner, faDownload, faHistory } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

// Replace with your actual template URL
const TEMPLATE_URL = 'https://public-testing.ap-south-1.linodeobjects.com/admin-fee-template.xlsx';

const UploadAdminFee = () => {
  const [files, setFiles] = useState([]);
  const [successfulUploads, setSuccessfulUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const allowedFileTypes = ['xlsx', 'xls'];
  const maxFileSizeMB = 20;
  const maxUploadLimit = 5;

  const handleFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(', ')} files allowed.`,
          { duration: 4000 }
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(
          `File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`,
          { duration: 4000 }
        );
        return false;
      }

      return true;
    });

    if (files.length + validFiles.length > maxUploadLimit) {
      toast.error(
        `You can only upload up to ${maxUploadLimit} files at a time.`,
        { duration: 4000 }
      );
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      uploadId: null,
      error: null,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    newFiles.forEach((fileObj) => handleFileUpload(fileObj));
  };

  const handleFileUpload = async (fileObj) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f
      )
    );

    try {
      const formData = new FormData();
      formData.append('file', fileObj.file);

      const response = await axios.post(`${BASE_URL}/admin-fee/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.file === fileObj.file ? { ...f, progress: percentCompleted } : f
            )
          );
        },
      });

      if (response.data && response.data.signed_url && response.data.upload_id) {
        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.file === fileObj.file
              ? {
                  ...f,
                  isUploading: false,
                  progress: 100,
                  uploadId: response.data.upload_id,
                }
              : f
          )
        );
        setSuccessfulUploads((prev) => [...prev, response.data.upload_id]);
        toast.success(`File ${fileObj.file.name} uploaded successfully!`, {
          duration: 4000,
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to upload file.';
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? { ...f, isUploading: false, error: errorMessage }
            : f
        )
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  const handleStartProcessing = async () => {
    if (successfulUploads.length === 0) {
      toast.error('No successfully uploaded files to process.', {
        duration: 4000,
      });
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/admin-fee/process`,
        { upload_ids: successfulUploads },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      toast.success(response.data.message, { duration: 4000 });
      setFiles([]);
      setSuccessfulUploads([]);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to start processing.';
      toast.error(errorMessage, { duration: 4000 });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileChange(droppedFiles);
  };

  const handleRemoveFile = async (fileObj) => {
    const result = await MySwal.fire({
      title: 'Remove File?',
      text: `Are you sure you want to remove ${fileObj.file.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, remove it',
    });

    if (result.isConfirmed) {
      setFiles((prevFiles) => prevFiles.filter((f) => f.file !== fileObj.file));
      if (fileObj.uploadId) {
        setSuccessfulUploads((prev) =>
          prev.filter((id) => id !== fileObj.uploadId)
        );
      }
      toast.success('File removed successfully.', { duration: 3000 });
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-200 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <div className="w-full max-w-1xl bg-white rounded-2xl shadow-2xl p-6 sm:p-8 lg:p-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Upload Admin Fee</h1>
          <button
            type="button"
            onClick={() => navigate('/admin-fee/processed')}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 shadow-sm"
            data-tooltip-id="history-tooltip"
            data-tooltip-content="View processed data transactions history"
          >
            <FontAwesomeIcon icon={faHistory} className="mr-2" />
            View Data Processed List
          </button>
        </div>

        {/* Main Content: Instructions and Drag-and-Drop Side-by-Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Instructions Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
              Excel File Format Instructions
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              Your Excel file must follow the provided template format. Below are the required and optional columns:
            </p>
            <div className="max-h-60 overflow-y-auto pr-2">
              <ul className="list-disc list-inside text-gray-600 text-sm space-y-2">
                <li>
                  <strong>LOA Date Issued</strong>: Date in <code>YYYY-MM-DD</code> format.{' '}
                  <span className="text-blue-400 font-semibold">[Optional]</span>
                </li>
                <li>
                  <strong>Admission Date</strong>: Date in <code>YYYY-MM-DD</code> format.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Discharged Date</strong>: Date in <code>YYYY-MM-DD</code> format.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Trans No.</strong>: Transaction number.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>LOA</strong>: Letter of Authorization code.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Member Name</strong>: Full name of the member.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Member Type</strong>: Type of member (e.g., Principal).{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Member ID</strong>: Unique identifier for the member.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>TIN</strong>: Tax Identification Number.{' '}
                  <span className="text-blue-400 font-semibold">[Optional]</span>
                </li>
                <li>
                  <strong>Provider</strong>: Healthcare provider name.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Physician</strong>: Physician's name.{' '}
                  <span className="text-blue-400 font-semibold">[Optional]</span>
                </li>
                <li>
                  <strong>Coverage</strong>: Type of coverage (e.g., OUTPATIENT-LABORATORY).{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Transaction Amt.</strong>: Transaction amount.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Actual Claim Amt.</strong>: Actual claim amount.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Withholding Tax</strong>: Withholding tax amount.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>PPD</strong>: Prompt Payment Discount.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>PPD Withholding Tax</strong>: PPD withholding tax amount.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Net Payable</strong>: Net payable amount.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Form #</strong>: Form number.{' '}
                  <span className="text-blue-400 font-semibold">[Optional]</span>
                </li>
                <li>
                  <strong>Biller Reference</strong>: Biller reference.{' '}
                  <span className="text-blue-400 font-semibold">[Optional]</span>
                </li>
                <li>
                  <strong>SOA</strong>: Statement of Account.{' '}
                  <span className="text-blue-400 font-semibold">[Optional]</span>
                </li>
                <li>
                  <strong>Corporate</strong>: Corporate name.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Date Credited</strong>: Date in <code>YYYY-MM-DD</code> format.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Check No.</strong>: Check number.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>CV#</strong>: Control Voucher number.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Payment Ref. No.</strong>: Payment reference number.{' '}
                  <span className="text-red-600 font-semibold">[Required]</span>
                </li>
                <li>
                  <strong>Account Name</strong>: Account name.{' '}
                  <span className="text-blue-400 font-semibold">[Optional]</span>
                </li>
                <li>
                  <strong>Mode of Payment</strong>: Payment method (e.g., FT, CHECK).{' '}
                  <span className="text-blue-400 font-semibold">[Optional]</span>
                </li>
              </ul>
            </div>
            <p className="text-sm text-gray-600 mt-4 mb-4">
              Download the template to use as a reference:
            </p>
            <a
              href={TEMPLATE_URL}
              download="admin-fee-template.xlsx"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 shadow-sm"
              data-tooltip-id="download-tooltip"
              data-tooltip-content="Download Excel template"
              aria-label="Download admin fee template"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download Template
            </a>
          </div>

          {/* Drag-and-Drop Area */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-6 sm:p-8 text-center transition-all duration-300 shadow-sm ${
              isDragging
                ? 'border-indigo-500 bg-indigo-50 scale-105 shadow-md'
                : 'border-gray-300 bg-gray-50 hover:shadow-md'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="region"
            aria-label="Drag and drop files here"
          >
            <FontAwesomeIcon
              icon={faUpload}
              className="text-4xl sm:text-5xl text-indigo-500 mb-4"
            />
            <p className="text-base sm:text-lg text-gray-700">
              Drag and drop Excel files here or{' '}
              <button
                type="button"
                onClick={handleChooseFile}
                className="text-indigo-600 font-semibold hover:text-indigo-800 transition-colors duration-200"
              >
                choose files
              </button>
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-2">
              Supported formats: .xlsx, .xls (Max {maxFileSizeMB}MB per file, up to {maxUploadLimit} files)
            </p>
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept=".xlsx,.xls"
              onChange={(e) => handleFileChange(e.target.files)}
              className="hidden"
              aria-hidden="true"
            />
          </div>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
              Selected Files
            </h2>
            <ul className="space-y-4">
              {files.map((fileObj, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-gray-100 p-4 rounded-lg shadow-sm transition-transform hover:shadow-md duration-200"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileObj.file.name}
                    </p>
                    {fileObj.isUploading && (
                      <div className="relative pt-2">
                        <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-indigo-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                            style={{ width: `${fileObj.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600 mt-1 block">
                          {fileObj.progress}% Uploading
                        </span>
                      </div>
                    )}
                    {fileObj.error && (
                      <p className="text-xs text-red-600 mt-1">
                        Error: {fileObj.error}
                      </p>
                    )}
                    {fileObj.uploadId && (
                      <p className="text-xs text-green-600 mt-1">
                        Uploaded (ID: {fileObj.uploadId})
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveFile(fileObj)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-200"
                    disabled={fileObj.isUploading}
                    data-tooltip-id="remove-tooltip"
                    data-tooltip-content="Remove file"
                    aria-label={`Remove ${fileObj.file.name}`}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end mt-6 space-x-3">
          <button
            type="button"
            onClick={() => {
              setFiles([]);
              setSuccessfulUploads([]);
              toast.success('All files cleared.', { duration: 3000 });
            }}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={files.length === 0}
            data-tooltip-id="clear-tooltip"
            data-tooltip-content="Clear all files"
          >
            Clear All
          </button>
          <button
            type="button"
            onClick={handleStartProcessing}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={successfulUploads.length === 0 || files.some((f) => f.isUploading)}
            data-tooltip-id="process-tooltip"
            data-tooltip-content="Start processing uploaded files"
          >
            Start Processing
          </button>
        </div>

        {/* Loading Modal */}
        {isProcessing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 flex flex-col items-center space-y-4 animate-pulse">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-4xl text-indigo-600 animate-spin"
              />
              <p className="text-lg font-medium text-gray-800">
                Processing Files...
              </p>
              <p className="text-sm text-gray-500">
                This may take a moment. Please wait.
              </p>
            </div>
          </div>
        )}

        {/* Tooltips */}
        <Tooltip id="remove-tooltip" place="top" />
        <Tooltip id="clear-tooltip" place="top" />
        <Tooltip id="process-tooltip" place="top" />
        <Tooltip id="download-tooltip" place="top" />
        <Tooltip id="history-tooltip" place="top" />
      </div>
    </div>
  );
};

export default UploadAdminFee;